import { api } from './api';

export interface OrganizationPermissions {
  menuPermissions: MenuPermissions;
  alertPermissions: AlertPermissions;
  canCreateAgents: boolean;
}

export interface MenuPermissions {
  canViewDashboard: boolean;
  canViewLeads: boolean;
  canViewConversations: boolean;
  canViewReports: boolean;
  canViewAgents: boolean;
  canViewTemplates: boolean;
  canViewCredits: boolean;
  canViewPlans: boolean;
  canViewUsers: boolean;
  canViewSegments: boolean;
  canViewSettings: boolean;
  canViewFinanceiro: boolean;
}

export interface AlertPermissions {
  showTrialAlerts: boolean;
  showPaymentAlerts: boolean;
  showWelcomeModals: boolean;
  showPlanInactiveAlerts: boolean;
}

export interface Organization {
  id: string;
  name: string;
  identifier: string;
  credit: number;
  currentPlan?: string;
  endIn?: string;
  subscriptionActive: boolean;
  permissions: OrganizationPermissions;
  active: boolean;
  isWhiteLabelManaged?: boolean;
  whiteLabelUserId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrganizationRQ {
  name: string;
  identifier: string;
  credit: number;
  currentPlan?: string;
  endIn?: string;
  permissions: OrganizationPermissions;
}

export interface OrganizationUpdateRQ {
  name?: string;
  identifier?: string;
  credit?: number;
  currentPlan?: string;
  endIn?: string;
  permissions?: OrganizationPermissions;
}

export interface CreateOrganizationWithUserRQ {
  organizationName: string;
  name: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  initialCredits?: number;
  permissions: OrganizationPermissions;
}

export const organizationApi = {
  async list(): Promise<Organization[]> {
    return api.get<Organization[]>('/Organization/GetAll', { auth: true });
  },

  async get(id: string): Promise<Organization> {
    return api.get<Organization>(`/Organization/Get/${id}`, { auth: true });
  },

  async create(data: OrganizationRQ): Promise<Organization> {
    return api.post<Organization>('/Organization/Create', data, { auth: true });
  },

  async update(id: string, data: OrganizationRQ): Promise<Organization> {
    return api.put<Organization>(`/Organization/Update/${id}`, data, { auth: true });
  },

  async patch(id: string, data: OrganizationUpdateRQ): Promise<Organization> {
    return api.patch<Organization>(`/Organization/Patch/${id}`, data, { auth: true });
  },

  async remove(id: string): Promise<void> {
    return api.delete<void>(`/Organization/Delete/${id}`, { auth: true });
  },

  async cancelSubscription(id: string): Promise<void> {
    return api.patch<void>(`/Organization/CancelSubscription/${id}`, {}, { auth: true });
  },

  async createWithUser(data: CreateOrganizationWithUserRQ): Promise<any> {
    return api.post<any>('/Authentication/CreateOrganizationWithUser', data, { auth: true });
  },

  getDefaultPermissions(): OrganizationPermissions {
    return {
      menuPermissions: {
        canViewDashboard: true,
        canViewLeads: true,
        canViewConversations: true,
        canViewReports: true,
        canViewAgents: true,
        canViewTemplates: true,
        canViewCredits: true,
        canViewPlans: true,
        canViewUsers: true,
        canViewSegments: true,
        canViewSettings: true,
        canViewFinanceiro: false,
      },
      alertPermissions: {
        showTrialAlerts: true,
        showPaymentAlerts: true,
        showWelcomeModals: true,
        showPlanInactiveAlerts: true,
      },
      canCreateAgents: true,
    };
  },
};
